import React, { useState, useRef, useEffect } from "react";
import loadable from "@loadable/component";
import { motion, AnimatePresence } from "framer-motion";
const ImageLoader = loadable(() => import("../common/ImageLoader"));
import { useIsMobile } from "../../hooks";

import graphPm from "assets/images/performanceRefresh/performance-chart/graph-pm.png";
import graphPmSm from "assets/images/performanceRefresh/performance-chart/graph-pm-sm.png";
import graphSmoke from "assets/images/performanceRefresh/performance-chart/graph-smoke.png";
import graphSmokeSm from "assets/images/performanceRefresh/performance-chart/graph-smoke-sm.png";
import graphDust from "assets/images/performanceRefresh/performance-chart/graph-dust.png";
import graphDustSm from "assets/images/performanceRefresh/performance-chart/graph-dust-sm.png";
import graphPetDander from "assets/images/performanceRefresh/performance-chart/graph-pet-dander.png";
import graphPetDanderSm from "assets/images/performanceRefresh/performance-chart/graph-pet-dander-sm.png";
import graphPollen from "assets/images/performanceRefresh/performance-chart/graph-pollen.png";
import graphPollenSm from "assets/images/performanceRefresh/performance-chart/graph-pollen-sm.png";
import graphVirus from "assets/images/performanceRefresh/performance-chart/graph-virus.png";
import graphVirusSm from "assets/images/performanceRefresh/performance-chart/graph-virus-sm.png";

import pmPng from "assets/images/performanceRefresh/performance-chart/pm-png.png";
import pmPngSm from "assets/images/performanceRefresh/performance-chart/pm-png-sm.png";
import smokePng from "assets/images/performanceRefresh/performance-chart/smoke-png.png";
import dustPng from "assets/images/performanceRefresh/performance-chart/dust-png.png";
import petDanderPng from "assets/images/performanceRefresh/performance-chart/pet-dander-png.png";
import virusPng from "assets/images/performanceRefresh/performance-chart/virus-png.png";
import virusPngSm from "assets/images/performanceRefresh/performance-chart/virus-png-sm.png";
import pollenPng from "assets/images/performanceRefresh/performance-chart/pollen-png.png";

/**
 * @returns {JSX.Element}
 * @constructor
 */

const stats = [
  {
    id: "virus",
    backgroundColor:
      "radial-gradient(234.21% 125.84% at 0.85% 91.15%, #033E40 0%, #131B1C 100%)",
    mainTitle: (
      <>
        She filters <strong>98.9% of viruses</strong>, helping to keep the
        sniffles out of your space.
      </>
    ),
    description: (
      <>
        Conducted in a 1,007 ft<sup>3</sup> chamber on Mila Air with the Overreactor filter with tests performed in accordance with ANSI/AHAM AC-1-2020 entitled "Association of Home Appliance Manufacturers Method for Measuring Performance of Portable Household Electric Room Air Cleaners."
      </>
    ),
    title: "😷🦠 Virus",
    image: graphVirus,
    imageSm: graphVirusSm,
    graphic: {
      image: virusPng,
      imageSm: virusPngSm,
    },
  },
  {
    id: "pm2",
    backgroundColor: "linear-gradient(245deg, #F0F0F0 7.48%, #FFF 58.71%)",
    mainTitle: (
      <>
        She filters <strong>99.9% of smog</strong>, because pollution has no
        place in your home.
      </>
    ),
    description:
      "While there are no standard lab test that measures the full range of PM2.5 (2.5µm), lab standard tests do measure smoke and dust, which covers this same range.",
    title: "🚕💨 PM2.5",
    image: graphPm,
    imageSm: graphPmSm,

    graphic: {
      image: pmPng,
      imageSm: pmPngSm,
    },
  },
  {
    id: "smoke",
    backgroundColor: "linear-gradient(245deg, #C4C3C0 7.48%, #FFF 58.71%)",
    mainTitle: (
      <>
        She filters <strong>99.9% of smoke</strong>, so you don't have to worry
        when the world's on fire.
      </>
    ),
    description: (
      <>
        Conducted in a 1,007 ft<sup>3</sup> chamber on Mila Air and the Basic
        Breather filter with tests performed in accordance with ANSI/AHAM
        AC-1-2020 entitled "Association of Home Appliance Manufacturers Method
        for Measuring Performance of Portable Household Electric Room Air
        Cleaners."
      </>
    ),
    title: "🔥💨 Smoke",
    image: graphSmoke,
    imageSm: graphSmokeSm,
    graphic: {
      image: smokePng,
    },
  },
  {
    id: "dust",
    backgroundColor: "linear-gradient(245deg, #C4C3C0 7.48%, #FFF 58.71%)",
    mainTitle: (
      <>
        She filters <strong>99.9% of dust</strong>, saving you from unnecessary
        sneezes.
      </>
    ),
    description: (
      <>
        Conducted in a 1,007 ft<sup>3</sup> chamber on Mila Air and the Basic
        Breather filter with tests performed in accordance with ANSI/AHAM
        AC-1-2020 entitled "Association of Home Appliance Manufacturers Method
        for Measuring Performance of Portable Household Electric Room Air
        Cleaners."
      </>
    ),
    title: "🧹💨 Dust",
    image: graphDust,
    imageSm: graphDustSm,
    graphic: {
      image: dustPng,
    },
  },
  {
    id: "dander",
    backgroundColor: "linear-gradient(237deg, #F3EFEC 0.62%, #FFF 39.43%)",
    mainTitle: (
      <>
        She filters <strong>99.9% of pet dander</strong>, so you and Fido can
        hang out without the hives.
      </>
    ),
    description: (
      <>
        Conducted in a 1,007 ft<sup>3</sup> chamber on Mila Air and the Critter
        Cuddler filter with tests performed in accordance with ANSI/AHAM
        AC-1-2020 entitled "Association of Home Appliance Manufacturers Method
        for Measuring Performance of Portable Household Electric Room Air
        Cleaners."
      </>
    ),
    title: (
      <>
        🐶🐱&nbsp;<span>Pet&nbsp;</span>Dander
      </>
    ),
    image: graphPetDander,
    imageSm: graphPetDanderSm,
    graphic: {
      image: petDanderPng,
    },
  },
  {
    id: "pollen",
    backgroundColor: "linear-gradient(241deg, #B4D4F1 11.2%, #FFF 77.21%)",
    mainTitle: (
      <>
        She filters <strong>99.9% of pollen</strong>, so you can make sure the
        outdoors stay out.
      </>
    ),
    description: (
      <>
        Conducted in a 1,007 ft<sup>3</sup> chamber on Mila Air and the Big
        Sneeze filter with tests performed in accordance with ANSI/AHAM
        AC-1-2020 entitled "Association of Home Appliance Manufacturers Method
        for Measuring Performance of Portable Household Electric Room Air
        Cleaners."
      </>
    ),
    title: "🌼🤧 Pollen",
    image: graphPollen,
    imageSm: graphPollenSm,
    graphic: {
      image: pollenPng,
    },
  },
];

const PerformanceChart = () => {
  const isMobile = useIsMobile(769);
  const containerRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState(() =>
    isMobile ? "smoke" : "virus",
  );

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const activeStats = stats.find((stat) => stat.id === activeFilter);
  useEffect(() => {
    setActiveFilter(isMobile ? "smoke" : "virus");
  }, [isMobile]);
  return (
    <section className={`performance-chart performance-chart--${activeFilter}`}>
      <div className="container">
        <div className="performance-chart__header">
          <h2 className="performance-chart__title">
            Yep, she’s a beast. Get a load of these performance numbers. 👀
          </h2>
          <p className="performance-chart__description">
            At Mila we put our filters through rigorous tests—and make the
            results available to anyone interested in these geeky details.{" "}
          </p>
        </div>
        <div
          className="performance-chart__card"
          style={{ background: activeStats.backgroundColor }}
        >
          <ImageLoader
            className="performance-chart__graphic"
            image={activeStats.graphic?.image}
            smallImage={activeStats.graphic?.imageSm}
            alt={`${activeFilter} graphic`}
          />
          <div className="performance-chart__chart-header">
            <h2 className="performance-chart__chart-title">
              {activeStats.mainTitle}
            </h2>
            {isMobile && (
              <ChartInfo isMobile={isMobile} className="for-small-screen" />
            )}
          </div>
          <div ref={containerRef} className="performance-chart__body">
            <div className="performance-chart__body-container">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeFilter}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.1 }}
                  style={{ position: "absolute" }}
                >
                  <ImageLoader
                    className="performance-chart__image img-relative"
                    placeholderAspectRatio={993 / 435}
                    svgSmUrl={activeStats.imageSm}
                    svgUrl={activeStats.image}
                    alt={`${activeFilter} chart`}
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          <div className="performance-chart__footer">
            <div className="performance-chart__info">
              <p className="performance-chart__info-text">
                {activeStats.description}
              </p>
              {!isMobile && (
                <ChartInfo isMobile={isMobile} className="for-large-screen" />
              )}
            </div>
            <div className="performance-chart__filter">
              <div className="performance-chart__filter-center">
                <ul className="performance-chart__filter-list">
                  {stats.map((stat) => (
                    <li key={stat.id}>
                      <button
                        type="button"
                        className={`button button--white button--shadow-dark button--lg ${
                          activeFilter === stat.id ? " active" : ""
                        }`}
                        onClick={() => handleFilterClick(stat.id)}
                      >
                        {stat.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="performance-chart__footer-bottom footer-content">
          <h4>Fun Fact 🤓</h4>
          <p>
            Milas detected potentially dangerous carbon monoxide (CO) levels for
            91 households in the past year.
          </p>
          {/* <a
            href="/performance"
            className="button button--white button--lg button--shadow-dark"
          >
            Dive into Mila's Performance
          </a> */}
        </div>
      </div>
    </section>
  );
};

const ChartInfo = ({ isMobile, className = "" }) => {
  return (
    <div className={`performance-chart__info-scheme ${className}`}>
      <p>
        {isMobile ? (
          <svg
            width="19"
            height="5"
            viewBox="0 0 19 5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.91321 2.10547L18.5859 2.10547"
              stroke="url(#paint0_linear_23877_2251)"
              strokeWidth="4"
            />
            <defs>
              <linearGradient
                id="paint0_linear_23877_2251"
                x1="0.986866"
                y1="0.401364"
                x2="18.5893"
                y2="0.598934"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0.0316992" stopColor="#F03358" />
                <stop offset="0.54" stopColor="#FFB216" />
                <stop offset="0.965" stopColor="#1CE4D5" />
              </linearGradient>
            </defs>
          </svg>
        ) : (
          <svg
            width="63"
            height="7"
            viewBox="0 0 63 7"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3.17954e-06 3.53125L63 3.53125"
              stroke="url(#paint0_linear_23657_2128)"
              strokeWidth="6"
            />
            <defs>
              <linearGradient
                id="paint0_linear_23657_2128"
                x1="0.262569"
                y1="1.82715"
                x2="62.9197"
                y2="4.33414"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0.0316992" stopColor="#F03358" />
                <stop offset="0.54" stopColor="#FFB216" />
                <stop offset="0.965" stopColor="#1CE4D5" />
              </linearGradient>
            </defs>
          </svg>
        )}
        Mila removal efficiency %
      </p>
      <p>
        {isMobile ? (
          <svg
            width="19"
            height="3"
            viewBox="0 0 19 3"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.803835 1.10547L18.4766 1.10547"
              stroke="url(#paint0_linear_23877_2254)"
              strokeWidth="2"
              strokeDasharray="3 3"
            />
            <defs>
              <linearGradient
                id="paint0_linear_23877_2254"
                x1="18.3203"
                y1="1.35156"
                x2="5.71488"
                y2="-4.14248"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#666666" />
                <stop offset="1" stopColor="#D7D7D7" />
              </linearGradient>
            </defs>
          </svg>
        ) : (
          <svg
            width="63"
            height="5"
            viewBox="0 0 63 5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M-3.69549e-06 2.53125L63 2.53125"
              stroke="url(#paint0_linear_23657_2131)"
              strokeWidth="4"
              strokeDasharray="8 8"
            />
            <defs>
              <linearGradient
                id="paint0_linear_23657_2131"
                x1="63"
                y1="2.53125"
                x2="47.8689"
                y2="-25.2918"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#666666" />
                <stop offset="1" stopColor="#D7D7D7" />
              </linearGradient>
            </defs>
          </svg>
        )}{" "}
        Natural decay
      </p>
    </div>
  );
};
export default PerformanceChart;
